/**
 * The lines a firm publishes, as rows that can be added, changed and removed.
 *
 * Shared by the branding page and the guided setup, because two editors of one list would
 * disagree first on the rule that matters: exactly one line is the one the apps dial.
 * Flagging a line here clears the flag on every other, so the server's "at most one" is
 * never something the person has to manage by hand.
 */

import { useTranslation } from 'react-i18next';

import { Button, FieldMessage, Icon, Input, Select, Switch } from '../../ui';
import { maxPhoneLines, phoneLineKinds, type PhoneLine } from '../api';
import { numberFieldOf } from './phoneLines';

export function PhoneLinesEditor({
  lines,
  onChange,
  disabled,
}: {
  lines: PhoneLine[];
  onChange: (lines: PhoneLine[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();

  const update = (index: number, change: Partial<PhoneLine>) =>
    onChange(lines.map((line, at) => (at === index ? { ...line, ...change } : line)));

  const flag = (index: number) =>
    onChange(lines.map((line, at) => ({ ...line, isSupportLine: at === index })));

  const remove = (index: number) => {
    const rest = lines.filter((_, at) => at !== index);

    // The flag follows the list: removing the flagged line hands it to the first that is
    // left, which is what the server would do anyway, shown before it happens.
    if (rest.length > 0 && !rest.some((line) => line.isSupportLine)) {
      rest[0] = { ...rest[0], isSupportLine: true };
    }

    onChange(rest);
  };

  const add = () =>
    onChange([
      ...lines,
      {
        label: '',
        number: '',
        kind: 'Booking',
        shownToPassengers: true,
        isSupportLine: lines.length === 0,
      },
    ]);

  return (
    <div className="flex flex-col">
      {lines.length === 0 ? (
        <p className="text-secondary text-ink-3 m-0 px-5 py-3">{t('branding.noLines')}</p>
      ) : (
        // Two rows per line: what it is above, what it does beneath. Six controls on one
        // row fit a wide settings page and squeeze the two text fields to nothing in the
        // guide's column, and the label and the number are the two a person types.
        lines.map((line, index) => (
          <div key={index} className="flex flex-col gap-2 px-5 py-3 border-b border-rule">
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(140px,0.8fr)] gap-3 items-start">
              <Input
                aria-label={t('branding.lineLabel')}
                placeholder={t('branding.lineLabel')}
                value={line.label}
                maxLength={40}
                disabled={disabled}
                onChange={(event) => update(index, { label: event.target.value })}
              />

              {/* The id is the API's name for this row's number, so a refusal about it —
                  missing, or not a full number — is written under this box and not in a
                  banner over the whole list (#360). */}
              <div className="flex flex-col min-w-0">
                <Input
                  id={numberFieldOf(index)}
                  aria-label={t('branding.lineNumber')}
                  placeholder={t('branding.lineNumber')}
                  type="tel"
                  value={line.number}
                  disabled={disabled}
                  onChange={(event) => update(index, { number: event.target.value })}
                />
                <FieldMessage id={numberFieldOf(index)} />
              </div>

              <Select
                aria-label={t('branding.lineKind')}
                value={line.kind}
                disabled={disabled}
                onChange={(event) => update(index, { kind: event.target.value as PhoneLine['kind'] })}
              >
                {phoneLineKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`branding.lineKinds.${kind}`)}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-secondary text-ink-2 whitespace-nowrap">
                <Switch
                  checked={line.shownToPassengers}
                  disabled={disabled}
                  label={t('branding.lineShown')}
                  onChange={(next) => update(index, { shownToPassengers: next })}
                />
                {t('branding.lineShown')}
              </label>

              {/* A radio in the shape of a button: one line carries the flag, and pressing
                  another moves it rather than adding a second. */}
              <Button
                size="sm"
                variant={line.isSupportLine ? 'primary' : 'outline'}
                aria-pressed={line.isSupportLine}
                disabled={disabled}
                onClick={() => flag(index)}
              >
                {line.isSupportLine && <Icon name="check" small />}
                {t('branding.lineSupport')}
              </Button>

              <span className="flex-1" />

              <Button
                size="sm"
                variant="quiet"
                icon
                disabled={disabled}
                title={t('branding.removeLine')}
                aria-label={t('branding.removeLine')}
                onClick={() => remove(index)}
              >
                <Icon name="close" />
              </Button>
            </div>
          </div>
        ))
      )}

      <div className="px-5 py-3">
        <Button size="sm" disabled={disabled || lines.length >= maxPhoneLines} onClick={add}>
          <Icon name="plus" small />
          {t('branding.addLine')}
        </Button>
      </div>
    </div>
  );
}
